import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import { ayurvedaService } from '../services/ayurvedaService';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();
  const navigate = useNavigate();
  const [treatmentCategories, setTreatmentCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Fetch treatment categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const result = await ayurvedaService?.getTreatmentCategories();
        if (result?.data) {
          setTreatmentCategories(result?.data);
        }
      } catch (error) {
        console.error('Failed to load treatment categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Handle category click - navigate to treatments page with category parameter
  const handleCategoryClick = (category) => {
    navigate(`/treatments?category=${category?.id}`, {
      state: { categoryId: category?.id, categoryName: category?.name }
    });
  };

  // Handle General Wellness click - navigate to home page and scroll to Service Universe with "All" category
  const handleGeneralWellnessClick = () => {
    // Navigate to home page with hash for Service Universe and "all" category
    navigate('/#service-universe-all');
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      const serviceUniverseSection = document.getElementById('service-universe');
      if (serviceUniverseSection) {
        serviceUniverseSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const footerSections = [
    {
      title: 'Services',
      links: loadingCategories
        ? [{ name: 'Loading...', href: '#', disabled: true }]
        : treatmentCategories?.length > 0
          ? [
            // Add General Wellness link at the top
            { name: 'General Wellness', onClick: handleGeneralWellnessClick },
            // Add treatment categories
            ...treatmentCategories?.map(category => ({
              name: category?.name,
              categoryId: category?.id,
              categorySlug: category?.slug,
              onClick: () => handleCategoryClick(category)
            }))
          ]
          : [
            { name: 'General Wellness', onClick: handleGeneralWellnessClick },
            { name: 'Auto-Immune Treatments', href: '/treatments' },
            { name: 'Spine & Disc Care', href: '/treatments' },
            { name: 'Skin Care', href: '/treatments' },
            { name: 'Metabolic Disorder Treatments', href: '/treatments' },
            { name: 'Arthritis & Joint Care', href: '/treatments' },
            { name: 'Stress & Health Management', href: '/treatments' }
          ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        //{ name: 'Our Team', href: '/about#team' },
        //{ name: 'Beauty Academy', href: '/beauty-academy' },
        //{ name: 'Careers', href: '/careers' },
        //{ name: 'Press', href: '/press' },
        //{ name: 'Blog', href: '/blog' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Contact Us', href: '/contact' },
        //{ name: 'Client Portal', href: '/client-portal' },
        //{ name: 'Booking Help', href: '/help/booking' },
        //{ name: 'Gift Cards', href: '/gift-cards' },
        //{ name: 'Loyalty Program', href: '/loyalty' },
        //{ name: 'FAQ', href: '/faq' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '/privacy-policy' },
        { name: 'Terms of Service', href: '/terms' },
        //{ name: 'Cookie Policy', href: '/cookies' },
        //{ name: 'Refund Policy', href: '/refund' },
        //{ name: 'Accessibility', href: '/accessibility' }
      ]
    }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: 'Instagram', href: 'https://www.instagram.com/ayurparishkarkendra/', followers: '25K' },
    { name: 'Facebook', icon: 'Facebook', href: 'https://www.facebook.com/ayurparishkarkendra/', followers: '15K' },
    { name: 'LinkedIn', icon: 'Linkedin', href: 'https://www.linkedin.com/in/sri-dhanvantari-ayurvaidik-parishkar-kendra-1506a1276/', followers: '30K' },
    // Temporarily commented social media links
    // { name: 'TikTok', icon: 'Music', href: 'https://tiktok.com/@morphicbeauty', followers: '30K' },
    // { name: 'YouTube', icon: 'Youtube', href: 'https://youtube.com/morphicbeauty', followers: '12K' },
    { name: 'Pinterest', icon: 'Pin', href: 'https://in.pinterest.com/ayurparishkarkendra/', followers: 'New' },
    { name: 'X', icon: 'X', href: 'https://x.com//ayurparishkar/', followers: 'New' }
  ];


  const contactInfo = {
    address: '1st floor, G94C+WWP BLOCK-1, LAKSHMI NARAYANA ESTATES, 24, High Tension Line Rd, Nadigada Tanda, Miyapur, Hyderabad, Telangana 500049',
    phone: '(784)220-7799 / (784)220-2255',
    email: 'ayurparishkarkendra@gmail.com',
    hours: {
      weekdays: 'Tue-Sun: 8:00 AM - 7:00 PM',
      break: '1:00 PM - 3:00 PM',
      weeklyoff: 'Mon: 10:00 AM - 6:00 PM'
    }
  };
  const primaryPhone = contactInfo?.phone
    ?.split('/')?.[0]
    ?.trim()
    ?.replace(/[^\d+]/g, '');
  return (
    <footer className="relative glass-panel text-foreground mt-20" style={{ background: 'rgba(47, 138, 76, 0.08)', backdropFilter: 'blur(15px)' }}>
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary"></div>

      {/* Subtle Pattern Overlay */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '30px 30px'
        }}>
      </div>

      {/* Floating Accent Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-64 h-64 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{ top: '10%', left: '5%' }}
        />
        <motion.div
          className="absolute w-48 h-48 bg-gradient-to-l from-accent/10 to-primary/10 rounded-full blur-3xl"
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
            scale: [1.2, 1, 1.2]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{ bottom: '10%', right: '10%' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        {/* Newsletter Section 
        <motion.div
          className="glass-card p-8 mb-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="font-headline text-2xl font-semibold mb-4 text-foreground">
            Stay Beautiful with Our Newsletter
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Get exclusive beauty tips, early access to new services, and special offers delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <Button
              variant="default"
              className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white font-medium px-6"
              iconName="Send"
              iconPosition="right"
              iconSize={16}
            >
              Subscribe
            </Button>
          </div>
        </motion.div>*/}

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6 mb-6">
          {/* Brand Section - Logo + Description */}
          <div className="md:col-span-2 lg:col-span-1">
            {/* Logo */}
            <div className="flex items-center mb-3">
              <div className="relative h-full py-1">
                <img
                  src="/assets/images/APK_LogoVector1-1765235042576.png"
                  alt="Sri Dhanwantari AyurVaidik Parishkar Kendra - Authentic Ayurvedic Healing Center"
                  className="h-full w-auto max-h-10 lg:max-h-18 object-cover" />
              </div>
              <div className="ml-3">
                <h2 className="font-headline text-xs lg:text-xs font-bold leading-tight text-foreground"
                >
                  Sri Dhanvantari Ayurvaidik Parishkar Kendra
                </h2>
                <p className="text-xs text-muted-foreground font-accent mt-0.5">
                  Authentic Health Sanctuary
                </p>
              </div>
            </div>
            <div>
              <p className="text-muted-foreground leading-relaxed text-sm">
                Experience holistic health like never before — time-tested Ayurvedic therapies, personalized care, and a focus on lasting immunity.
              </p>
            </div>
          </div>
          {/* Footer Links - Services, Company, Support */}
          {footerSections?.map((section) => (
            <div
              key={section?.title}
              className="lg:col-span-1"
            >
              <h3 className="font-semibold text-foreground mb-2 text-sm uppercase tracking-wide">{section?.title}</h3>
              <ul className="space-y-1.5">
                {section?.links?.map((link) => (
                  <li key={link?.name}>
                    {link?.onClick ? (
                      <button
                        onClick={link?.onClick}
                        disabled={link?.disabled}
                        className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed hover:translate-x-1 transform inline-block font-normal"
                      >
                        {link?.name}
                      </button>
                    ) : (
                      <a
                        href={link?.href}
                        className="text-muted-foreground hover:text-primary transition-all duration-200 text-sm hover:translate-x-1 transform inline-block font-normal"
                      >
                        {link?.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact Info + Business Hours + Social Media Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 pb-6 border-t border-slate-700/50 pt-6">
          {/* Business Hours */}
          <div className="p-3 bg-[#5a9ed3]/80 backdrop-blur-sm rounded-lg border border-slate-700/50 hover:border-primary/50 transition-colors duration-300">
            <div className="flex items-center justify-center mb-3">
              <Icon name="Clock" size={20} className="text-primary mr-2" />
              <h4 className="font-semibold text-foreground text-sm uppercase tracking-wide">
                Business Hours
              </h4>
            </div>
            <div className="text-lg text-foreground space-y-2 text-center">
              <p className="font-medium">{contactInfo?.hours?.weekdays}</p>
              <p className="text-sm text-foreground">Break: {contactInfo?.hours?.break}</p>
              {/* <p className="text-foreground">{contactInfo?.hours?.weeklyoff}</p> */}
            </div>
          </div>

          {/* Contact Info - Consolidated */}
          <div className="p-3 bg-[#eb9724]/80 backdrop-blur-sm rounded-lg border border-slate-700/50 hover:border-primary/50 transition-colors duration-300">
            <div className="flex items-center justify-center mb-3">
              <Icon name="Phone" size={20} className="text-primary mr-2" />
              <h4 className="font-semibold text-foreground text-sm uppercase tracking-wide">Contact Us</h4>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-start group">
                <Icon name="MapPin" size={16} className="text-primary mr-2 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-foreground text-xs leading-relaxed">{contactInfo?.address}</span>
              </div>
              <div className="flex items-center group">
                <Icon name="Phone" size={16} className="text-primary mr-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <a href={`tel:${primaryPhone}`} className="text-foreground text-xs hover:text-primary transition-colors duration-200">{contactInfo?.phone}</a>
              </div>
              <div className="flex items-center group">
                <Icon name="Mail" size={16} className="text-primary mr-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <a href={`mailto:${contactInfo?.email}`} className="text-foreground text-xs hover:text-primary transition-colors duration-200 break-all">{contactInfo?.email}</a>
              </div>
            </div>
          </div>

          {/* Social Media Section */}
          <motion.div
            className="p-3 bg-[#97b943]/80 backdrop-blur-sm rounded-lg border border-slate-700/50 hover:border-primary/50 transition-colors duration-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex items-center justify-center mb-3">
              <Icon name="Share2" size={20} className="text-primary mr-2" />
              <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">Follow Us</h3>
            </div>
            <div className="flex flex-wrap gap-2.5 justify-center">
              {socialLinks?.map((social) => (
                <motion.a
                  key={social?.name}
                  href={social?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/80 backdrop-blur-sm border border-primary/30 hover:border-primary/60 p-3 rounded-lg text-center cursor-pointer group transition-all duration-300 shadow-sm"
                  whileHover={{ scale: 1.05, y: -3 }}
                  transition={{ duration: 0.3 }}
                >
                  <Icon
                    name={social?.icon}
                    size={24}
                    className="text-primary mx-auto mb-1 group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="text-xs font-semibold text-foreground">{social?.followers}</div>
                  <div className="text-[10px] text-foreground/70">{social?.name}</div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>


        {/* Bottom Bar */}
        <motion.div
          className="border-t border-slate-700/50 pt-4 pb-2 flex flex-col md:flex-row justify-between items-center gap-3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="text-xs text-foreground/80 text-center md:text-left">
            © {currentYear} Sri Dhanvantari Ayurvaidik Parishkar Kendra. All rights reserved.
            <span className="hidden md:inline"> Crafted with <span className="text-red-500">❤️</span> for health enthusiasts.</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <div className="flex items-center space-x-1.5 text-[10px] text-foreground/80">
              <Icon name="Shield" size={14} className="text-primary" />
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center space-x-1.5 text-[10px] text-foreground/80">
              <Icon name="Award" size={14} className="text-primary" />
              <span>Licensed & Certified</span>
            </div>
            <div className="flex items-center space-x-1.5 text-[10px] text-foreground/80">
              <Icon name="Heart" size={14} className="text-primary" />
              <span>5-Star Rated</span>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;